import { Link } from "react-router-dom";
import { useCart } from "@/App";
import { Button } from "@/components/ui/button";
import { ShoppingCart, GitCompare, Eye, Plus } from "lucide-react";

export default function ProductCard({ product }) {
  const { addToCart, addToCompare } = useCart();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-UY', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  return (
    <article
      className="bg-white rounded-lg overflow-hidden group shadow-sm hover:shadow-lg transition-all duration-300 border border-transparent hover:border-primary/20"
      data-testid={`product-card-${product.product_id}`}
    >
      {/* Image */}
      <Link to={`/producto/${product.product_id}`} className="block relative aspect-square overflow-hidden bg-gray-50">
        {product.images?.[0] ? (
          <img
            src={product.images[0]}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-gray-100">
            <span className="text-sm">Sin imagen</span>
          </div>
        )}
        
        {/* Featured badge */}
        {product.is_featured && (
          <span className="absolute top-3 left-3 bg-accent text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow-sm">
            Destacado
          </span>
        )}

        {/* Quick action buttons */}
        <div className="absolute top-3 right-3 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <button
            onClick={(e) => {
              e.preventDefault();
              addToCompare(product);
            }}
            className="w-9 h-9 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
            aria-label="Comparar"
          >
            <GitCompare className="w-4 h-4" />
          </button>
        </div>

        {/* Quick add to cart - bottom overlay */}
        <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <Button
            onClick={(e) => {
              e.preventDefault();
              addToCart(product.product_id);
            }}
            disabled={product.stock <= 0}
            size="sm"
            className="w-full bg-white text-primary hover:bg-accent hover:text-white font-semibold text-sm h-9"
          >
            <Plus className="w-4 h-4 mr-1" />
            Agregar al carrito
          </Button>
        </div>
      </Link>

      {/* Content */}
      <div className="p-4">
        {/* Brand */}
        {product.brand && (
          <p className="text-xs text-primary/60 font-medium uppercase tracking-wider mb-1">
            {product.brand}
          </p>
        )}

        {/* Name */}
        <Link to={`/producto/${product.product_id}`}>
          <h3 className="font-medium text-sm leading-snug line-clamp-2 text-foreground hover:text-primary transition-colors min-h-[2.5rem]">
            {product.name}
          </h3>
        </Link>

        {/* Price and stock */}
        <div className="mt-3 flex items-end justify-between">
          <div>
            <p className="font-bold text-xl text-foreground">
              {formatPrice(product.price)}
            </p>
            <p className={`text-xs mt-0.5 ${product.stock > 0 ? 'text-green-600' : 'text-red-500'}`}>
              {product.stock > 0 ? 'En stock' : 'Sin stock'}
            </p>
          </div>
        </div>
      </div>
    </article>
  );
}
